// Shared constants for the Marketing & Media Command Centre.
// Keep in sync with backend routes/marketing_command_centre.py.

import {
  Instagram, Facebook, Youtube, Linkedin, Mail, Globe,
  Palette, DollarSign, Music,
} from 'lucide-react';

export const IDEA_STAGES = [
  'new_idea', 'under_review', 'approved', 'scheduled', 'in_production',
  'editing', 'awaiting_approval', 'changes_required', 'ready_to_publish',
  'published', 'completed',
];

export const REQUEST_STAGES = [
  'submitted', 'acknowledged', 'in_progress', 'awaiting_approval',
  'changes_required', 'ready_to_publish', 'published', 'completed', 'declined',
];

export const PROJECT_STAGES = [
  'planning', 'script_required', 'script_ready', 'scheduled',
  'ready_to_film', 'filmed', 'raw_uploaded', 'editing_in_progress',
  'first_draft_uploaded', 'changes_requested', 'revised_draft_uploaded',
  'final_edit_uploaded', 'final_approved', 'publishing_details_required',
  'scheduled_to_publish', 'published', 'completed', 'archived',
];

export const FILMING_STAGES = [
  'requested', 'script_required', 'script_ready', 'scheduled',
  'ready_to_film', 'filmed', 'raw_uploaded', 'completed',
];

export const CONTENT_TYPES = [
  { v: 'short_video', l: 'Short-form video' },
  { v: 'long_video', l: 'Long-form video' },
  { v: 'static_post', l: 'Static social post' },
  { v: 'carousel', l: 'Carousel' },
  { v: 'advertisement', l: 'Advertisement' },
  { v: 'email', l: 'Email' },
  { v: 'webinar', l: 'Webinar' },
  { v: 'podcast', l: 'Podcast' },
  { v: 'graphic', l: 'Graphic' },
  { v: 'article', l: 'Article' },
  { v: 'landing_page', l: 'Landing page' },
  { v: 'website_update', l: 'Website update' },
  { v: 'branding_asset', l: 'Branding asset' },
];

export const PLATFORMS = [
  { v: 'instagram', l: 'Instagram', icon: Instagram, tone: '#e1306c', bg: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' },
  { v: 'facebook', l: 'Facebook', icon: Facebook, tone: '#1877f2', bg: 'linear-gradient(135deg,#4267B2,#1877f2)' },
  { v: 'tiktok', l: 'TikTok', icon: Music, tone: '#111111', bg: 'linear-gradient(135deg,#010101,#25f4ee,#fe2c55)' },
  { v: 'youtube', l: 'YouTube', icon: Youtube, tone: '#ff0000', bg: 'linear-gradient(135deg,#ff0000,#c4302b)' },
  { v: 'linkedin', l: 'LinkedIn', icon: Linkedin, tone: '#0a66c2', bg: 'linear-gradient(135deg,#0a66c2,#004182)' },
  { v: 'email_newsletter', l: 'Email Newsletter', icon: Mail, tone: '#4f46e5', bg: 'linear-gradient(135deg,#6366f1,#4338ca)' },
  { v: 'website', l: 'Website', icon: Globe, tone: '#059669', bg: 'linear-gradient(135deg,#059669,#065f46)' },
  { v: 'paid_ads', l: 'Paid Ads', icon: DollarSign, tone: '#f59e0b', bg: 'linear-gradient(135deg,#f59e0b,#b45309)' },
  { v: 'branding', l: 'Branding', icon: Palette, tone: '#7c3aed', bg: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' },
];

export const PRIORITIES = [
  { v: 'low', l: 'Low', tone: '#94a3b8' },
  { v: 'normal', l: 'Normal', tone: '#4f46e5' },
  { v: 'high', l: 'High', tone: '#f59e0b' },
  { v: 'urgent', l: 'Urgent', tone: '#e11d48' },
];

// Status colour palette (per the brief):
//   Grey — Not started · Blue — In progress · Purple — Scheduled
//   Orange — Awaiting approval · Red — Urgent / overdue / changes required
//   Light green — Approved / ready · Dark green — Published / completed
export const STATUS_TONE = {
  // Ideas
  new_idea:                { bg: '#f1f5f9', ink: '#334155', accent: '#64748b', label: 'New Idea' },
  under_review:            { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Under Review' },
  approved:                { bg: '#dcfce7', ink: '#166534', accent: '#22c55e', label: 'Approved' },
  scheduled:               { bg: '#ede9fe', ink: '#5b21b6', accent: '#7c3aed', label: 'Scheduled' },
  in_production:           { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'In Production' },
  editing:                 { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Editing' },
  awaiting_approval:       { bg: '#ffedd5', ink: '#9a3412', accent: '#f97316', label: 'Awaiting Approval' },
  changes_required:        { bg: '#fee2e2', ink: '#991b1b', accent: '#ef4444', label: 'Changes Required' },
  ready_to_publish:        { bg: '#dcfce7', ink: '#166534', accent: '#22c55e', label: 'Ready to Publish' },
  published:               { bg: '#065f46', ink: '#ecfdf5', accent: '#10b981', label: 'Published' },
  completed:               { bg: '#065f46', ink: '#ecfdf5', accent: '#10b981', label: 'Completed' },
  // Requests
  submitted:               { bg: '#f1f5f9', ink: '#334155', accent: '#64748b', label: 'Submitted' },
  acknowledged:            { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Acknowledged' },
  in_progress:             { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'In Progress' },
  declined:                { bg: '#fee2e2', ink: '#991b1b', accent: '#ef4444', label: 'Declined' },
  // Projects
  planning:                { bg: '#f1f5f9', ink: '#334155', accent: '#64748b', label: 'Planning' },
  script_required:         { bg: '#fef3c7', ink: '#78350f', accent: '#d97706', label: 'Script Required' },
  script_ready:            { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Script Ready' },
  ready_to_film:           { bg: '#ede9fe', ink: '#5b21b6', accent: '#7c3aed', label: 'Ready to Film' },
  filmed:                  { bg: '#ede9fe', ink: '#5b21b6', accent: '#7c3aed', label: 'Filmed' },
  raw_uploaded:            { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Raw Uploaded' },
  editing_in_progress:     { bg: '#dbeafe', ink: '#1e40af', accent: '#3b82f6', label: 'Editing in Progress' },
  first_draft_uploaded:    { bg: '#ffedd5', ink: '#9a3412', accent: '#f97316', label: 'First Draft Uploaded' },
  changes_requested:       { bg: '#fee2e2', ink: '#991b1b', accent: '#ef4444', label: 'Changes Requested' },
  revised_draft_uploaded:  { bg: '#ffedd5', ink: '#9a3412', accent: '#f97316', label: 'Revised Draft Uploaded' },
  final_edit_uploaded:     { bg: '#ffedd5', ink: '#9a3412', accent: '#f97316', label: 'Final Edit Uploaded' },
  final_approved:          { bg: '#dcfce7', ink: '#166534', accent: '#22c55e', label: 'Final Approved' },
  publishing_details_required: { bg: '#dcfce7', ink: '#166534', accent: '#22c55e', label: 'Publishing Details Required' },
  scheduled_to_publish:    { bg: '#ede9fe', ink: '#5b21b6', accent: '#7c3aed', label: 'Scheduled to Publish' },
  archived:                { bg: '#e2e8f0', ink: '#475569', accent: '#94a3b8', label: 'Archived' },
  // Filming
  requested:               { bg: '#f1f5f9', ink: '#334155', accent: '#64748b', label: 'Requested' },
};

export const platformMeta = (v) => PLATFORMS.find((p) => p.v === v) || null;
export const contentTypeLabel = (v) => (CONTENT_TYPES.find((c) => c.v === v) || {}).l || v;
export const priorityMeta = (v) => PRIORITIES.find((p) => p.v === v) || PRIORITIES[1];
export const statusTone = (v) => STATUS_TONE[v] || { bg: '#f1f5f9', ink: '#334155', accent: '#64748b', label: (v || '—').replace(/_/g, ' ') };

export const humanDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export const humanDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const daysBetween = (a, b = new Date()) => {
  if (!a) return null;
  const d1 = new Date(a); const d2 = new Date(b);
  const ms = d2.getTime() - d1.getTime();
  return Math.floor(ms / 86400000);
};

export const dueBadge = (dueIso) => {
  if (!dueIso) return null;
  const d = daysBetween(new Date(), dueIso);
  if (d === null || Number.isNaN(d)) return null;
  if (d < 0) return { label: `Overdue by ${Math.abs(d)}d`, tone: '#e11d48', bg: '#fee2e2' };
  if (d === 0) return { label: 'Due today', tone: '#b45309', bg: '#fef3c7' };
  if (d <= 2) return { label: `Due in ${d}d`, tone: '#b45309', bg: '#fef3c7' };
  return { label: `Due in ${d}d`, tone: '#334155', bg: '#e2e8f0' };
};
